/** Options for the CSV / Sheets export builders. */
export interface CsvExportOptions {
    includeGrammes?: boolean;
    includeContent?: boolean;
}

const buildCsvQuery = (options: CsvExportOptions): string => {
    const params = new URLSearchParams();
    if (options.includeGrammes) params.append("include_grammes", "true");
    if (options.includeContent) params.append("include_content", "true");
    const qs = params.toString();
    return qs ? `?${qs}` : "";
};

/**
 * Builders for every Gateway API path (relative to the gateway base URL).
 *
 * Each entry returns the URL string for one endpoint, interpolating path
 * params and, where relevant, appending the CSV/Sheets export query string.
 */
export const gatewayEndpoints = {
    /** Clients collection: list (`GET`) and create (`POST /clients`). */
    client:        () => `/clients`,
    /** Single client by id: read, update or delete (`/clients/{id}`). */
    clientById:    (id: number) => `/clients/${id}`,

    /** Exchange a Google OAuth code for gateway session cookies. */
    authGoogleExchange: () => `/auth/google/exchange`,
    /** Log the current user out and clear their session cookies. */
    authLogout:         () => `/auth/logout`,
    /** Fetch the authenticated user's profile. */
    authMe:             () => `/auth/me`,
    /** Refresh the access token from the refresh-token cookie. */
    authRefresh:        () => `/auth/refresh`,
    /** List the current user's own active device sessions. */
    authSessions:       () => `/auth/sessions`,
    /** Revoke one of the current user's own sessions by id. */
    authRevokeSession:  (sessionId: string) => `/auth/sessions/${encodeURIComponent(sessionId)}/revoke`,
    /**
     * The current user's Google grant: `GET` mints an access token for the pickers,
     * `DELETE` revokes the grant at Google and drops the stored tokens.
     */
    authGoogleToken:    () => `/auth/google-token`,

    /** Operation-level metadata for a given YTG operation. */
    ytgMetaByOperation:    (ytgOperationId: number | string) => `/ytg-meta/operation/${ytgOperationId}`,
    /** Set the Google Drive target folder for an operation's uploads. */
    ytgMetaSetFolder:      (ytgOperationId: number | string) => `/ytg-meta/operation/${ytgOperationId}/folder`,
    /** Per-row metadata for a given YTG operation. */
    ytgRowMetaByOperation: (ytgOperationId: number | string) => `/ytg-meta/operation/${ytgOperationId}/rows`,
    /** Paginated list of all operations with joined metadata. */
    ytgMetaOperationsAll:  () => `/ytg-meta/operations/all`,
    /** Aggregated operation statistics (server-side GROUP BY). */
    ytgMetaOperationsStats: () => `/ytg-meta/operations/stats`,

    /** Per-domain website-extraction stats (admin only). */
    extractionStats: () => `/ytg/admin/extraction-stats`,
    /** On-demand single-URL text extractor (admin/tm). */
    textExtraction: () => `/ytg/tools/extract`,
    /** Enriched CSV export of an operation (download). */
    ytgMetaOperationCsv: (ytgOperationId: number | string, options: CsvExportOptions = {}) =>
        `/ytg-meta/operation/${ytgOperationId}/csv${buildCsvQuery(options)}`,
    /** Export an operation to a Google Sheet. */
    ytgMetaOperationSheets: (ytgOperationId: number | string, options: CsvExportOptions = {}) =>
        `/ytg-meta/operation/${ytgOperationId}/sheets${buildCsvQuery(options)}`,

    /** Column headers of a source spreadsheet, used before import. */
    ytgMetaSheetHeaders:        (spreadsheetId: string) => `/ytg-meta/sheets/${spreadsheetId}/headers`,
    /** Preview the rows that would be imported from a spreadsheet. */
    ytgOperationPreviewSheet:   () => `/ytg/operation/preview-sheet`,
    /** Create an operation from a Google Sheet source. */
    ytgOperationFromSheet:      () => `/ytg/operation/from-sheet`,
    /** Create an operation from an explicit list of rows. */
    ytgOperationFromRows:       () => `/ytg/operation/from-rows`,
    /** Tell which Google Docs the app may read for the current user (drive.file). */
    ytgOperationGoogleDocsAccess: () => `/ytg/operation/google-docs/access`,

    /** Upload all guide PDFs of an operation to Google Drive. */
    ytgUploadAllGuides: () => `/drive/upload-all-guides`,
    /** Upload all graph images of an operation to Google Drive. */
    ytgUploadAllGraphs: () => `/drive/upload-all-graphs`,

    /** Proxy a remote image through the gateway (CORS/mixed-content workaround). */
    proxyImage: (imageUrl: string) => `/proxy/image?url=${encodeURIComponent(imageUrl)}`,

    /**
     * Admin: list all users.
     *
     * Archived accounts are left out unless `includeArchived` is set: their rows only
     * survive because operations reference them, so they would otherwise pile up.
     */
    adminUsers:              (includeArchived = false) =>
        includeArchived ? `/admin/users?include_archived=true` : `/admin/users`,
    /** Admin: change a user's role. */
    adminUserRole:           (userId: number) => `/admin/users/${userId}/role`,
    /** Admin: activate or deactivate a user. */
    adminUserActive:         (userId: number) => `/admin/users/${userId}/active`,
    /** Admin: revoke all of a given user's sessions. */
    adminUserRevokeSessions: (userId: number) => `/admin/users/${userId}/revoke-sessions`,
    /** Admin: revoke every session of every user (global logout). */
    adminRevokeAllSessions:  () => `/admin/users/revoke-all-sessions`,
    /** Admin: revoke a given user's Google grant and drop their stored tokens. */
    adminUserRevokeGoogleToken: (userId: number) => `/admin/users/${userId}/revoke-google-token`,
    /** Admin: revoke every user's Google grant (used for a scope migration). */
    adminRevokeAllGoogleTokens: () => `/admin/users/revoke-all-google-tokens`,
    /** Admin: archive an account (offboarding; there is no account deletion). */
    adminUserArchive:        (userId: number) => `/admin/users/${userId}/archive`,
    /** Admin: list a given user's active device sessions. */
    adminUserSessions:       (userId: number) => `/admin/users/${userId}/sessions`,
    /** Admin: revoke one specific session of a given user. */
    adminRevokeUserSession:  (userId: number, sessionId: string) =>
        `/admin/users/${userId}/sessions/${encodeURIComponent(sessionId)}/revoke`,
};
